import { useCallback, useEffect, useMemo, useState } from "react";
import { useFetch } from "hooks/fetch/useFetch";

/**
 * Performs a get request on the url provided
 * @param url the url to get from
 * @param isBlocked if true a new request is not sent until it becomes false
 * @param onCompleted callback that is fired when the get completes successfully
 * @param onError callback that is fired when the get errors
 * @returns {{response: undefined, loading: boolean, error: boolean, refetch: ((function(*=, *=): void)|*)}}
 * loading - whether the request is currently in progress.
 *
 * error - whether the request is currently in an error state.
 *
 * response - the json response object, undefined if request not completed yet, errored or empty
 *
 * refetch - a callback function to refetch from the url.
 */
export function useFetchGet(
  url,
  isBlocked = false,
  onCompleted = undefined,
  onError = undefined
) {
  const [{ loading, error, response }, setFetchState] = useState({
    loading: true,
    error: false,
    response: undefined
  });
  const abortController = useMemo(() => {
    return new AbortController();
  }, []);

  // aborts any re-fetches currently in progress when the component unmounts
  useEffect(() => {
    return () => abortController.abort();
  }, [abortController]);

  const { callbackFunction } = useFetch(url);

  const getCallback = useCallback(
    (
      signal = abortController.signal,
      headers = { "Content-Type": "application/json" }
    ) => {
      setFetchState(prevState => {
        return { ...prevState, loading: true, error: false };
      });
      callbackFunction({
        method: "GET",
        headers,
        signal
      }).then(
        requestResponse => {
          setFetchState({
            loading: false,
            error: false,
            response: requestResponse
          });
          onCompleted && onCompleted(response);
        },
        error => {
          setFetchState({ loading: false, error: true, response: undefined });
          onError && onError(error);
        }
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [abortController.signal, callbackFunction]
  );

  useEffect(() => {
    // create an abort controller which will just be used in this request
    const abortController = new AbortController();

    if (!isBlocked) {
      getCallback(abortController.signal);
    } else {
      setFetchState({ loading: false, error: false, response: undefined });
    }

    // requests will be aborted if new requests come in before the old are finished
    // or when the component is unmounted
    return () => abortController.abort();
  }, [getCallback, isBlocked]);

  return {
    loading,
    error,
    response,
    refetch: getCallback
  };
}
